
import React, { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Check, X } from 'lucide-react';
import { 
  Command, 
  CommandEmpty, 
  CommandGroup, 
  CommandInput, 
  CommandItem, 
  CommandList
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

interface Item {
  id: string;
  name: string;
}

interface MultiSelectComboBoxProps {
  items: Item[];
  selectedItems: string[];
  onChange: (selectedIds: string[]) => void;
  placeholder: string;
  emptyMessage: string;
  disabled?: boolean;
  maxDisplayCount?: number;
}

export const MultiSelectComboBox = ({
  items,
  selectedItems,
  onChange,
  placeholder,
  emptyMessage,
  disabled = false,
  maxDisplayCount = 3
}: MultiSelectComboBoxProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  const selectedItemsData = items.filter(item => selectedItems.includes(item.id));
  const displayItems = selectedItemsData.slice(0, maxDisplayCount);
  const remainingCount = selectedItemsData.length - maxDisplayCount;

  const handleSelect = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      onChange(selectedItems.filter(id => id !== itemId));
    } else {
      onChange([...selectedItems, itemId]);
    }
    setInputValue('');
  };

  const handleRemove = (itemId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedItems.filter(id => id !== itemId));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={`w-full justify-start h-auto min-h-10 px-3 py-2 text-left ${
            selectedItems.length > 0 ? 'flex flex-wrap gap-1 items-center' : ''
          }`}
          onClick={() => setOpen(true)}
        >
          {selectedItems.length > 0 ? (
            <>
              {displayItems.map(item => (
                <Badge 
                  key={item.id} 
                  variant="secondary"
                  className="flex items-center gap-1 py-1 pl-2 pr-1"
                >
                  {item.name}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={(e) => handleRemove(item.id, e)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))}
              {remainingCount > 0 && (
                <Badge variant="outline" className="ml-1">
                  +{remainingCount} more
                </Badge>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0 shadow-md rounded-md border border-input">
        <Command>
          <CommandInput 
            placeholder="Search..." 
            value={inputValue}
            onValueChange={setInputValue}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              <ScrollArea className="max-h-60">
                {items
                  .filter(item => 
                    item.name.toLowerCase().includes(inputValue.toLowerCase())
                  )
                  .map(item => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => handleSelect(item.id)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <div className={`flex h-4 w-4 items-center justify-center rounded-sm border ${
                        selectedItems.includes(item.id) 
                          ? 'bg-primary border-primary' 
                          : 'border-input'
                      }`}>
                        {selectedItems.includes(item.id) && (
                          <Check className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                      <span>{item.name}</span>
                    </CommandItem>
                  ))}
              </ScrollArea>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
